import React from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle2,
  Phone,
  Mail,
  User,
  Home,
  Loader2,
  ArrowRight,
  Ticket,
  ShieldCheck,
  RefreshCw,
} from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { Contact } from '@/lib/marketplace';

/**
 * ───────────────────────────────────────────────────────────────────────────
 * LEAD UNLOCKED — the post-purchase destination.
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Shown on the project page itself the moment a lead unlocks, whether it was
 * paid for with the $29 single purchase or with a membership credit. It is
 * deliberately NOT a separate "thank you" route:
 *
 *   • the customer is already looking at the exact project they bought, so
 *     there is nothing to find again and nothing to click to "continue"
 *   • the approved contact details are IN this panel, not one scroll away
 *   • every obvious next action is here — Call, Email, My Leads, Browse more
 *
 * THREE HONEST STATES, never a dead end:
 *   contact present   → confirmation + contact details + next actions
 *   contact loading   → "Releasing the contact details…" with a spinner
 *   contact missing   → the purchase is still confirmed (the entitlement row
 *                       exists server-side); offer a one-tap retry instead of
 *                       a silent empty box. This is what a brief read-replica
 *                       lag looks like, and it resolves on a tap or a refresh.
 */

interface Props {
  /** Approved contact record, once the entitlement has been proven. */
  contact: Contact | null;
  /** True while the contact record is being fetched. */
  revealing?: boolean;
  /** Re-fetch the contact record (retry after a transient read failure). */
  onRetry?: () => void;
  /** Where "View My Leads" goes (the contractor dashboard). */
  dashboardHref: string;
  /** Where "Browse more projects" goes. */
  jobsHref: string;
}

const LeadUnlockedPanel: React.FC<Props> = ({
  contact,
  revealing = false,
  onRetry,
  dashboardHref,
  jobsHref,
}) => {
  const { t } = useI18n();

  const actionBase =
    'inline-flex min-h-[48px] flex-1 items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-bold transition-colors';

  return (
    <section
      // Announced to screen readers the instant it appears, so the outcome is
      // never something the customer has to go looking for.
      role="status"
      aria-live="polite"
      className="mb-6 overflow-hidden rounded-2xl border-2 border-emerald-300 bg-emerald-50"
    >
      <div className="px-5 py-4">
        <p className="flex items-center gap-2 text-base font-extrabold text-emerald-900 sm:text-lg">
          <CheckCircle2 className="h-5 w-5 shrink-0" /> {t('pay.successTitle')}
        </p>
        <p className="mt-1.5 text-sm leading-relaxed text-emerald-800">{t('pay.successBody')}</p>
      </div>

      {/* ── APPROVED CONTACT DETAILS ─────────────────────────────────────── */}
      <div className="border-t border-emerald-200 bg-white px-5 py-4">
        <p className="text-xs font-bold uppercase tracking-wide text-emerald-700">
          {t('jobs.contactUnlocked')}
        </p>

        {contact ? (
          <div className="mt-3 grid gap-2.5 sm:grid-cols-2">
            <p className="flex items-center gap-2 text-sm font-semibold text-slate-800">
              <User className="h-4 w-4 shrink-0 text-emerald-600" />
              {contact.contact_name}
            </p>
            <a
              href={`tel:${contact.phone}`}
              className="flex items-center gap-2 text-sm font-semibold text-slate-800 hover:text-emerald-700"
            >
              <Phone className="h-4 w-4 shrink-0 text-emerald-600" />
              {contact.phone}
            </a>
            <a
              href={`mailto:${contact.email}`}
              className="flex items-center gap-2 break-all text-sm font-semibold text-slate-800 hover:text-emerald-700"
            >
              <Mail className="h-4 w-4 shrink-0 text-emerald-600" />
              {contact.email}
            </a>
            <p className="flex items-start gap-2 text-sm text-slate-700">
              <Home className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
              {contact.address}
            </p>
          </div>
        ) : revealing ? (
          <p className="mt-3 flex items-center gap-2 text-sm font-semibold text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> {t('pay.revealing')}
          </p>
        ) : (
          /* The payment IS settled and the entitlement exists — this is a read
             hiccup, so the customer gets a real action rather than a blank. */
          <div className="mt-3">
            <p className="text-sm font-semibold text-slate-600">{t('pay.revealRetry')}</p>
            {onRetry && (
              <button
                type="button"
                onClick={onRetry}
                style={{ touchAction: 'manipulation' }}
                className="mt-2 inline-flex min-h-[44px] items-center gap-1.5 rounded-xl border-2 border-emerald-300 px-4 py-2 text-sm font-bold text-emerald-800 hover:bg-emerald-50"
              >
                <RefreshCw className="h-4 w-4" /> {t('pay.revealRetryCta')}
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── NEXT ACTIONS — nobody has to wonder "what now?" ──────────────── */}
      <div className="border-t border-emerald-200 bg-white px-5 py-4">
        <p className="text-xs font-bold uppercase tracking-wide text-slate-400">
          {t('pay.nextSteps')}
        </p>
        <div className="mt-3 flex flex-col gap-2.5 sm:flex-row sm:flex-wrap">
          {/* Call and Email are only rendered when there is something real to
              dial or mail — a button that opens an empty tel: link is worse
              than no button. */}
          {contact?.phone && (
            <a
              href={`tel:${contact.phone}`}
              style={{ touchAction: 'manipulation' }}
              className={`${actionBase} bg-emerald-600 text-white hover:bg-emerald-700`}
            >
              <Phone className="h-4 w-4" /> {t('pay.callNow')}
            </a>
          )}
          {contact?.email && (
            <a
              href={`mailto:${contact.email}`}
              className={`${actionBase} border-2 border-emerald-300 text-emerald-800 hover:bg-emerald-50`}
            >
              <Mail className="h-4 w-4" /> {t('pay.emailNow')}
            </a>
          )}
          <Link to={dashboardHref} className={`${actionBase} bg-slate-900 text-white hover:bg-slate-800`}>
            <Ticket className="h-4 w-4" /> {t('pay.viewMyLeads')}
          </Link>
          <Link
            to={jobsHref}
            className={`${actionBase} border-2 border-slate-300 text-slate-700 hover:bg-slate-50`}
          >
            {t('pay.browseMore')} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {/* The one thing every buyer wants to know after paying: it is mine,
            permanently, on every device. */}
        <p className="mt-3 flex items-start gap-1.5 text-xs leading-relaxed text-slate-500">
          <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
          {t('pay.savedForever')}
        </p>
      </div>
    </section>
  );
};

export default LeadUnlockedPanel;
